import React, { useEffect, useMemo, useState } from "react";
import { Context, Hooks, Util } from "@xt/xtbase";
import { get_strategies, get_strategyReturnTrend } from "@/api/aicopy";
import {
  IconArrowRight,
  IconBot,
  IconLayers,
  IconLineChart,
  IconShieldCheck,
  IconSparkleAI,
  IconUsers,
  IconZap,
} from "./icons";
import HomePcGlowBackdrop from "./GlowBackdrop";
import HeroShowcase from "./HeroShowcase";
import AiLabSection from "./AiLabSection";
import MarketsSection from "./MarketsSection";
import DownloadSection from "./DownloadSection";
import styles from "./index.module.less";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getUrl } = Util;

const STATS = [
  { key: "assets", value: "200+" },
  { key: "exec", value: "<30ms" },
  { key: "markets", value: "5" },
];

function parseStrategies(response) {
  if (!response) return [];
  if (response.code === 0 && Array.isArray(response.data)) return response.data;
  if (response.rc === 0 && Array.isArray(response.result)) return response.result;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.result)) return response.result;
  return [];
}

function getReturn90dNum(strategy) {
  const raw = strategy?.return90d ?? strategy?.return_90d;
  const n = parseFloat(String(raw ?? "").replace(/%/g, "").trim());
  return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
}

function pickLeadByReturn90d(list) {
  if (!Array.isArray(list) || !list.length) return null;
  return list.reduce((best, cur) => (getReturn90dNum(cur) > getReturn90dNum(best) ? cur : best));
}

function parseReturnTrend(response) {
  if (!response || response.rc !== 0 || !response.result) return [];
  if (Array.isArray(response.result)) return response.result;
  return [response.result];
}

/**
 * PC 营销首页 — 对齐 spikex-ai-web app/(site)/page.tsx
 * 仅在 ≥769px 由 pages/index.js 挂载；手机钱包首页不动。
 */
export default function HomePcView() {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const isLogin = appState?.loginStatus === 1;
  const [strategies, setStrategies] = useState([]);
  const [strategyReturnTrends, setStrategyReturnTrends] = useState({});

  useEffect(() => {
    let cancelled = false;
    get_strategies({
      params: { limit: 20, offset: 0 },
      isResFull: true,
    })
      .then((res) => {
        if (cancelled) return;
        setStrategies(parseStrategies(res));
      })
      .catch(() => {
        if (!cancelled) setStrategies([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const leadStrategy = useMemo(() => pickLeadByReturn90d(strategies), [strategies]);

  // 与 /aicopy AI Strategy 一致：GET .../return-trend?days=90
  useEffect(() => {
    if (!strategies.length) return undefined;
    let cancelled = false;
    const ids = [
      ...new Set(
        [leadStrategy?.strategyId, ...strategies.slice(0, 4).map((s) => s.strategyId)].filter(Boolean)
      ),
    ];

    ids.forEach(async (strategyId) => {
      try {
        const response = await get_strategyReturnTrend(strategyId, 90, { isResFull: true });
        if (cancelled) return;
        const trendData = parseReturnTrend(response);
        if (!trendData.length) return;
        setStrategyReturnTrends((prev) => ({ ...prev, [strategyId]: trendData }));
      } catch (e) {
        console.error(`home pc return-trend ${strategyId}`, e);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [strategies, leadStrategy?.strategyId]);

  const registerHref = getUrl("/accounts/register");
  const aicopyHref = getUrl("/aicopy");
  const aiHref = getUrl("/ai-lab?tab=arena");
  const primaryHref = aicopyHref;
  const ctaPrimaryHref = isLogin ? aicopyHref : registerHref;
  const ctaPrimaryLabel = isLogin
    ? t("home.pcStartTrading") || "Start trading"
    : t("home.signUp") || "Sign up";

  const features = [
    {
      icon: IconBot,
      title: t("home.pcFeatAgents") || "AI Agents",
      desc: t("home.pcFeatAgentsDesc") || "Deploy autonomous agents that trade for you, around the clock.",
    },
    {
      icon: IconUsers,
      title: t("home.pcFeatArena") || "Arena",
      desc: t("home.pcFeatArenaDesc") || "Watch frontier AI models compete live, then deploy the best.",
    },
    {
      icon: IconLineChart,
      title: t("home.pcFeatFutures") || "Futures & Spot",
      desc: t("home.pcFeatFuturesDesc") || "Trade USDT-M futures and spot with deep liquidity and low fees.",
    },
    {
      icon: IconLayers,
      title: t("home.pcFeatUnified") || "Unified Account",
      desc: t("home.pcFeatUnifiedDesc") || "Crypto, stocks, commodities & forex in a single balance and wallet.",
    },
  ];

  const statLabels = {
    assets: t("home.pcStatAssets") || "Assets supported",
    exec: t("home.pcStatExec") || "Avg. execution",
    markets: t("home.pcStatMarkets") || "Markets",
  };

  return (
    <div className={`${styles.pageShell} ${styles.bgGrid} ${styles.spikexPc}`}>
      <HomePcGlowBackdrop />
      <div className={styles.page}>
        <section className={styles.hero}>
          <div className={styles.heroGrid}>
            <div>
              <span className={styles.pill}>
                <span className={styles.liveDot} />{" "}
                {t("home.pcHeroBadge") || "The AI-native exchange is live"}
              </span>
              <h1 className={styles.heroTitle}>
                {t("home.pcHeroTitleBefore") || "Trade smarter with"}{" "}
                <span className={styles.heroBrand}>SpikeX AI</span>
              </h1>
              <p className={styles.heroDesc}>
                {t("home.pcHeroDesc") ||
                  "The AI-native exchange for Crypto, Stocks, Commodities & Forex/Futures — discover AI agents, deploy them, and let them trade in your one account."}
              </p>
              <div className={styles.heroActions}>
                <a href={primaryHref} className={styles.btnPrimary}>
                  {isLogin
                    ? t("home.pcStartTrading") || "Start trading"
                    : t("home.pcStartTrading") || "Start trading"}{" "}
                  <IconArrowRight size={16} />
                </a>
                <a href={aiHref} className={styles.btnSecondary}>
                  <IconSparkleAI size={22} className={styles.brandIcon} />
                  {t("home.pcExploreAiLab") || "Explore AI Lab"}
                </a>
              </div>
              <dl className={styles.statsStrip}>
                {STATS.map((s) => (
                  <div key={s.key} className={styles.statsCell}>
                    <dd className={styles.statsValue}>{s.value}</dd>
                    <dt className={styles.statsLabel}>{statLabels[s.key]}</dt>
                  </div>
                ))}
              </dl>
            </div>
            <HeroShowcase
              leadStrategy={leadStrategy}
              returnTrend={
                leadStrategy?.strategyId ? strategyReturnTrends[leadStrategy.strategyId] : null
              }
            />
          </div>
        </section>

        <AiLabSection strategies={strategies} strategyReturnTrends={strategyReturnTrends} />
        <MarketsSection />

        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitleLg}>
              {t("home.pcFeaturesTitle") || "One platform, every market"}
            </h2>
            <a href={getUrl("/market")} className={styles.sectionLink}>
              {t("home.pcExploreMarkets") || "Explore markets"} <IconArrowRight size={16} />
            </a>
          </div>
          <div className={styles.featuresGrid}>
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className={`${styles.card} ${styles.cardHover} ${styles.featureCard}`}>
                  <span className={styles.featureIconWrap}>
                    <Icon size={20} className={styles.brandIcon} />
                  </span>
                  <h3 className={styles.cardTitle}>{f.title}</h3>
                  <p className={styles.cardDesc}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        <DownloadSection />

        <section className={styles.ctaSection}>
          <div className={`${styles.card} ${styles.ctaCard}`}>
            <div className={styles.ctaGlow} aria-hidden />
            <div className={styles.ctaInner}>
              <div>
                <div className={styles.ctaIcons}>
                  <IconShieldCheck size={20} className={styles.brandIcon} />
                  <IconZap size={20} className={styles.brandIcon} />
                </div>
                <h2 className={styles.ctaTitle}>
                  {t("home.pcCtaTitle") || "Start trading in minutes"}
                </h2>
                <p className={styles.ctaDesc}>
                  {t("home.pcCtaDesc") ||
                    "Create your account, deposit, and let AI strategies work for you."}
                </p>
              </div>
              <div className={styles.ctaActions}>
                <a href={ctaPrimaryHref} className={styles.btnPrimary}>
                  {ctaPrimaryLabel} <IconArrowRight size={16} />
                </a>
                <a href={aiHref} className={styles.btnSecondary}>
                  {t("home.pcExploreAiLab") || "Explore AI Lab"}
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
